import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "../../components/Button";
import { ApiError, api } from "../../lib/api";
import type {
  AlertDecision,
  CameraResponse,
  ReportRowResponse,
  ReportSummaryResponse,
} from "../../lib/api";
import { formatClockInZone, formatInZone } from "../../lib/storeTime";
import { severityBucket } from "../CameraView/severity";
import { ReportCsvDialog } from "./ReportCsvDialog";
import { type ParamChanges, ReportFiltersBar } from "./ReportFiltersBar";
import styles from "./ReportsPanel.module.css";
import { filtersFromParams, reportRange } from "./reportFilters";

type ReportsPanelProps = {
  storeId: string;
  timeZone: string;
  cameras: CameraResponse[];
};

const ALERT_LABEL: Record<AlertDecision, string> = { convey: "Sent", suppress: "Suppressed" };

const OUTCOME_LABEL: Record<AlertDecision, string> = {
  convey: "Alert sent",
  suppress: "Alert suppressed",
};

const periodLink = (row: ReportRowResponse): string =>
  `/cameras/${row.camera_id}?period=${encodeURIComponent(row.period_id)}`;

const duration = (row: ReportRowResponse): string => {
  const seconds = Math.round(
    (Date.parse(row.period_end_ts) - Date.parse(row.period_start_ts)) / 1000,
  );
  return seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`;
};

const ReportRowDetails = ({ row, timeZone }: { row: ReportRowResponse; timeZone: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard
      ?.writeText(`${window.location.origin}${periodLink(row)}`)
      .then(() => setCopied(true));
  };
  return (
    <div className={styles.details}>
      {row.reason && <p>{row.reason}</p>}
      <p className={styles.detailsMeta}>
        {row.decision ? OUTCOME_LABEL[row.decision] : "No alert"} ·{" "}
        {formatClockInZone(row.period_start_ts, timeZone)}–
        {formatClockInZone(row.period_end_ts, timeZone)} ({duration(row)})
        {row.camera_location ? ` · ${row.camera_location}` : ""}
      </p>
      <div className={styles.detailsLinks}>
        <Link to={periodLink(row)}>Open on camera page</Link>
        <button type="button" className={styles.linkButton} onClick={copy}>
          {copied ? "Link copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
};

const reportError = (e: unknown): string => {
  if (e instanceof ApiError && e.status === 403)
    return "Reports need an active plan for this store.";
  return e instanceof ApiError ? e.message : "Couldn't load the report.";
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

export const summaryLine = (s: ReportSummaryResponse): string =>
  [
    plural(s.total, "period"),
    `${plural(s.sent, "alert")} sent`,
    `${s.suppressed} suppressed`,
    ...s.by_severity.filter((b) => b.severity !== null).map((b) => `Sev ${b.severity}: ${b.count}`),
  ].join(" · ");

export const ReportsPanel = ({ storeId, timeZone, cameras }: ReportsPanelProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.toString();
  const filters = useMemo(() => filtersFromParams(new URLSearchParams(query), Date.now()), [query]);
  const [rows, setRows] = useState<ReportRowResponse[]>([]);
  const [summary, setSummary] = useState<ReportSummaryResponse | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csvOpen, setCsvOpen] = useState(false);
  // The filters the rows on screen belong to; a page fetched for older filters is dropped.
  const shownFilters = useRef(filters);

  const changeParams = useCallback(
    (changes: ParamChanges) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          next.delete(key);
          for (const v of value === null ? [] : [value].flat()) next.append(key, v);
        }
        return next;
      }),
    [setSearchParams],
  );

  useEffect(() => {
    let alive = true;
    shownFilters.current = filters;
    setError(null);
    api
      .getStoreReport(storeId, filters, undefined)
      .then((page) => {
        if (!alive) return;
        setRows(page.rows);
        setSummary(page.summary);
        setCursor(page.next_cursor);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setRows([]);
        setSummary(null);
        setCursor(null);
        setError(reportError(e));
      });
    return () => {
      alive = false;
    };
  }, [storeId, filters]);

  const showMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const next = await api.getStoreReport(storeId, filters, cursor);
      if (shownFilters.current !== filters) return;
      setRows((prev) => [...prev, ...next.rows]);
      setCursor(next.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className={styles.panel}>
      <div className={styles.toolbar}>
        <ReportFiltersBar
          filters={filters}
          range={reportRange(new URLSearchParams(query))}
          timeZone={timeZone}
          cameras={cameras}
          onChange={changeParams}
        />
        <div className={styles.actions}>
          <Button onClick={() => setCsvOpen(true)}>Download CSV</Button>
        </div>
      </div>
      {csvOpen && (
        <ReportCsvDialog
          storeId={storeId}
          filters={filters}
          timeZone={timeZone}
          rowCount={summary?.total ?? null}
          onClose={() => setCsvOpen(false)}
        />
      )}
      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
      {summary && (
        <div className={styles.summary} data-testid="report-summary">
          {summaryLine(summary)}
        </div>
      )}
      <div className={styles.tableWrap}>
        <table className={styles.table} aria-label="Store report">
          <thead>
            <tr>
              <th>Sev</th>
              <th>Time</th>
              <th>Camera</th>
              <th>Category</th>
              <th>Description</th>
              <th>Alert</th>
              <th>Open</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = `${row.camera_id}/${row.period_id}`;
              const open = key === openKey;
              return (
                <Fragment key={key}>
                  <tr data-open={open}>
                    <td>
                      {row.severity !== null && (
                        <span className={styles.sev} data-severity={severityBucket(row.severity)}>
                          {row.severity}
                        </span>
                      )}
                    </td>
                    <td className={styles.nowrap}>{formatInZone(row.period_start_ts, timeZone)}</td>
                    <td>{row.camera_name}</td>
                    <td>{row.category ?? "–"}</td>
                    <td className={styles.description}>
                      <button
                        type="button"
                        className={styles.rowToggle}
                        aria-expanded={open}
                        onClick={() => setOpenKey(open ? null : key)}
                      >
                        {row.description ?? "No description yet"}
                      </button>
                    </td>
                    <td data-decision={row.decision ?? "none"} className={styles.alert}>
                      {row.decision ? ALERT_LABEL[row.decision] : "–"}
                    </td>
                    <td>
                      <Link
                        to={periodLink(row)}
                        className={styles.open}
                        aria-label={`Open ${row.camera_name} at ${formatInZone(row.period_start_ts, timeZone)}`}
                      >
                        ↗
                      </Link>
                    </td>
                  </tr>
                  {open && (
                    <tr className={styles.detailsRow}>
                      <td colSpan={7}>
                        <ReportRowDetails row={row} timeZone={timeZone} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {loaded && !error && rows.length === 0 && (
        <p className={styles.empty}>No periods match these filters.</p>
      )}
      <div className={styles.footer}>
        {summary && (
          <span>
            Showing {rows.length} of {summary.total}
          </span>
        )}
        {cursor && (
          <Button
            variant="outline"
            size="sm"
            disabled={loadingMore}
            onClick={() => void showMore()}
          >
            Show more
          </Button>
        )}
      </div>
      <p className={styles.note}>
        All times in store time ({timeZone}). Clip-by-clip notes are kept 30 days; open a period on
        its camera page to see them.
      </p>
    </section>
  );
};
